<script>
   /****************************************************************/
   /* PlotFomHeatmap                                               */
   /* --------------------                                         */
   /* shows heatmap with FoM values.                               */
   /****************************************************************/

   import { Rectangles, Heatmap, ColormapLegend, TextLabels, Axes, XAxis, YAxis, Box } from 'svelte-plots-basic/2d';
   import { trimNum } from 'svelte-plots-basic/utils';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { csvRow, downloadCSV, escapeText } from '@mdatools-apps/shared/utils';
   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // get model object from properties
   let classmodel = $derived(props ? props.classmodel : null);

   let statItem = $derived(props ? props.statMat[params.resID][classmodel.rule] : null);
   let stat = $derived(statItem ? statItem.stat : null);
   let ticks = $derived(statItem ? statItem.ticks : null);
   let tickLabels = $derived(statItem ? statItem.tickLabels : null);
   let yaxisValues = $derived(statItem ? statItem.values : null);

   let curP = $derived(classmodel.rule === 'hard' ? classmodel.threshold : 1 - Number.parseFloat(classmodel.alpha)/100);
   let curY = $derived(yaxisValues ? yaxisValues.length - yaxisValues.v.findIndex(v => Math.abs(v - curP) < 0.00001) : null);
   let scalars = $derived(stat ? (classmodel.rule === 'hard' ? stat[params.fom] : stat[classmodel.limType][params.fom]) : null);

   const breaks = [0, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.99, 1.0];
   const colmap = [
      '#6200DF', // < -2
      '#6923FF', // -2.00 to -1.00
      '#648FFF', // -1.00 to  0.00
      '#8CCDFD', //  0.00 to  0.25
      '#D2ECFF', //  0.25 to  0.50
      '#FFF5E5', //  1.25 to  1.50
      '#FFE5BE', //  1.50 to  1.75
      '#FFCE85', //  1.75 to  2.00
      '#FFA34E', //  2.00 to  2.50
      '#FF8024', //  2.50 to  3.00
      '#CF5903'  //  > 3.0
   ];

   const titles = {
      'sens': 'Sensitivity',
      'spec': 'Specificity',
      'eff': 'Efficiency'
   }

   const labels = breaks.map(v => v.toFixed(v > 0.9 ? 2 : 1));

   const dY = $derived(scalars ? scalars.nrows / 8 : 1);
   // a cell of room on both sides of the grid's outer centres: the value under a selected cell
   // in the last column was cut by the plot area with less
   const limX = $derived(scalars ? [0, scalars.ncols + 1] : [0, 1]);
   const limY = $derived(scalars ? [-dY/2, scalars.nrows + Math.max(0.9, dY)] : [0, 1]);
   // the hard rule's figures are the first class's (the second's are the same, sensitivity and
   // specificity swapped), the soft rule's the totals
   const figuresOf = $derived(classmodel.rule === 'hard' ? classmodel.classNames[0] : 'total');
   const title = $derived(titles[params.fom] + ' (' + figuresOf + ')');
   const yLabel = $derived(classmodel.rule === 'hard' ? 'Threshold' : 'Alpha, %');
   const xLabel = 'Number of LVs, A';

   // the clicked cell, [row, column] from 1. the box and the value under it are derived from
   // it and the matrix shown, so a change of the fom or of the result set reads the new value
   let cell = $state(null);

   // the router hands an empty list after 'unselect', never null
   $effect(() => {
      if (!selected || selected.length !== 2) cell = null;
   });

   $effect(() => {
      if (classmodel.rule) callbacks.unselect();
   });

   // the two rules have grids of different heights, and the rule effect above clears the
   // selection one flush after the matrix changed: a cell beyond the matrix shown draws nothing
   const shown = $derived(selected && selected.length === 2 && cell && scalars &&
      cell[0] <= scalars.nrows && cell[1] <= scalars.ncols);
   const sel = $derived(shown ? [cell[1] - 0.5, scalars.nrows - cell[0] + 1.5] : null);
   const selFom = $derived(shown ?
      scalars.v[(cell[1] - 1) * scalars.nrows + (cell[0] - 1)].toFixed(3) : null);

   // the threshold, or alpha in %, of row r of the grid, row 1 on top
   function rowValue(r) {
      const v = yaxisValues.v[r - 1];
      return trimNum(classmodel.rule === 'hard' ? v : 100 * (1 - v));
   }

   function handleClick(r, c) {
      cell = [r, c];
      callbacks.select([trimNum(c), rowValue(r)]);
   }

   // row and column steps of the arrow keys, row 1 on top
   const steps = {ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0]};

   // arrow keys move the selected cell by one and do nothing at the edge of the grid. they reach
   // the plot while it has the focus, which a click on a cell gives; with a modifier, or with no
   // cell selected, a key is left to the browser
   function handleKey(e) {
      const d = steps[e.key];
      if (!d || !shown || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      const r = cell[0] + d[0], c = cell[1] + d[1];
      if (r >= 1 && r <= scalars.nrows && c >= 1 && c <= scalars.ncols) handleClick(r, c);
   }

   // name of the image and CSV files
   const fileName = 'plot-statheatmap';

   // method for creating CSV version of the plot data: one row per row of the grid, top to bottom
   function downloadCSVCallback() {
      const { sep, dec } = props.data[params.resID];
      const nrows = scalars.nrows;
      let out = csvRow([yLabel, ...Array.from({length: scalars.ncols}, (v, c) => c + 1)], sep, dec);
      for (let r = 1; r <= nrows; r++) {
         const row = Array.from({length: scalars.ncols}, (v, c) => scalars.v[c * nrows + r - 1]);
         out += csvRow([rowValue(r), ...row], sep, dec);
      }
      downloadCSV(out, fileName);
   }
</script>

{#if scalars }
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="heatmap-keys" tabindex="-1" onkeydown={handleKey}>
<Axes
   {limX} {limY} title={escapeText(title)} {fileName}
   plotActions={[{text: '↓ csv', callback: downloadCSVCallback}]}
   onclick={callbacks.unselect}
   downloadLinks="hover"
   margins={MARGINS}
   pngWidth={PNG_WIDTH}
   pngHeight={PNG_HEIGHT}
   pngRes={PNG_RES}
   >

   <Heatmap values={scalars} {breaks} {colmap} onclick={handleClick}/>
   <ColormapLegend {breaks} {colmap} {labels}/>
   <XAxis label={xLabel} whole={true} />
   <YAxis label={yLabel} whole={true} {ticks} {tickLabels} />
   <Rectangles left={[classmodel.ncomp - 0.5]} top={[curY + 0.5]} width={[1]} height={[1]} faceColor="transparent" lineColor="#221110" lineWidth={1.2}/>
   {#if sel}
      <Rectangles left={[sel[0]]} top={[sel[1]]} width={[1]} height={[1]} lineWidth={2} faceColor="transparent" lineColor="#221100"/>
      <TextLabels xValues={[sel[0] + 0.5]} yValues={[sel[1] - 0.5]} labels={selFom} pos={1} textSize={0.8} faceColor="#221100"/>
   {/if}
   <Box />
</Axes>
</div>
{/if}

<style>
   .heatmap-keys {
      width: 100%;
      height: 100%;
      outline: none;
   }
</style>
